import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { RogeMascota, RogeMascotaHeader } from "@/components/RogeMascota";
import { api } from "@/lib/api";
import { MenuUsuario } from "@/components/MenuUsuario";
import { OfertaPassword } from "@/components/OfertaPassword";
import { useExpediente } from "@/context/ExpedienteContext";
import { NavLink as Link2 } from "react-router-dom";
import { AlertTriangle, AlertOctagon } from "lucide-react";
import { FileText, ClipboardList, FolderOpen, Users, CheckSquare, FileSignature, Lock, Award, Briefcase, HelpCircle, BookOpen } from "lucide-react";
import { BarraMovil } from "@/components/BarraMovil";
import { Contactanos } from "@/components/Contactanos";

const ORDEN_MOVIL = ["/", "/hoja-vida", "/documentos", "/contratacion"];

const NAV = [
  { to: "/", label: "Mi expediente", corto: "Inicio", icon: FileText, end: true },
  { to: "/hoja-vida", label: "Mi hoja de vida", corto: "Hoja de vida", icon: ClipboardList },
  { to: "/vinculacion", label: "Mi vinculación", icon: Briefcase },
  { to: "/documentos", label: "Mis documentos", corto: "Documentos", icon: FolderOpen, correccion: true },
  { to: "/beneficiarios", label: "Mis beneficiarios", icon: Users, correccionBenef: true },
  { to: "/pendientes", label: "Mis pendientes", icon: CheckSquare },
  { to: "/contratacion", label: "Mi contratación", corto: "Contratación", icon: FileSignature },
  { to: "/certificados", label: "Mis certificados", icon: Award },
  { to: "/llamados", label: "Llamados de atención", corto: "Llamados", icon: AlertOctagon },
  { to: "/reglamento", label: "Reglamento Interno de Trabajo", icon: BookOpen, aviso: true },
];

function useAvisoReglamento(pathname) {
  const [pendiente, setPendiente] = useState(false);
  useEffect(() => {
    const cargar = () => api.get("/reglamento/estado").then((r) => setPendiente(!!r.data.pendiente)).catch(() => {});
    cargar();
    window.addEventListener("rit-confirmado", cargar);
    return () => window.removeEventListener("rit-confirmado", cargar);
  }, [pathname]);
  return pendiente;
}

export default function PortalLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Revalida la sesión en cada cambio de página (si el empleado fue retirado, el interceptor lo saca).
  useEffect(() => { api.get("/auth/me").catch(() => {}); }, [pathname]);
  const ritPendiente = useAvisoReglamento(pathname);
  const { exp } = useExpediente() || {};
  const correcciones = (exp?.documentos || []).filter((d) => d.estado === "correccion_requerida" && !d.is_deleted);
  const corrBenef = correcciones.filter((d) => d.beneficiario_id).length;
  const corrDocs = correcciones.length - corrBenef;
  const badge = (n) => (n.aviso && ritPendiente) || (n.correccion && corrDocs) || (n.correccionBenef && corrBenef) || 0;
  const pos = (n) => (ORDEN_MOVIL.includes(n.to) ? ORDEN_MOVIL.indexOf(n.to) : 99);
  const movil = [...NAV].sort((a, b) => pos(a) - pos(b)).map((n) => ({ ...n, badge: badge(n) }));

  const Sidebar = (
    <div className="flex h-full flex-col bg-vino-900 text-white">
      <div className="flex items-center gap-3 px-5 py-5">
        <div className="flex items-center justify-center rounded-lg bg-white px-2 py-1.5"><img src="/human-logo-full.png" alt="Human Resources" className="h-7 w-auto" /></div>
      </div>
      <div className="shrink-0"><RogeMascota testid="gato-roge-portal" /></div>
      <nav className="sidebar-scroll min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-2" data-testid="portal-sidebar-nav">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            data-testid={`pnav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-vino-800 text-white border-r-4 border-vino-400" : "text-vino-100/80 hover:bg-vino-800/60 hover:text-white"}`}>
            <n.icon className="h-[18px] w-[18px] shrink-0" /> <span className="flex-1">{n.label}</span>
            {n.aviso && ritPendiente && <span data-testid="pnav-reglamento-aviso" className="shrink-0 animate-pulse rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase text-vino-900">Nuevo</span>}
            {n.correccion && corrDocs > 0 && <span data-testid="pnav-correcciones-aviso" className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">{corrDocs}</span>}
            {n.correccionBenef && corrBenef > 0 && <span data-testid="pnav-correcciones-benef-aviso" className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">{corrBenef}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="shrink-0 space-y-2 px-3 pb-2">
        <Contactanos tipo="empleado" />
        <a href="/manuales/manual_empleado_roge.pdf" target="_blank" rel="noopener noreferrer" data-testid="pnav-guia-portal"
          className="flex items-center gap-3 rounded-lg border border-vino-700 px-3 py-2.5 text-sm font-medium text-vino-100 transition-colors hover:bg-vino-800/60 hover:text-white">
          <HelpCircle className="h-[18px] w-[18px]" /> Cómo usar mi portal
        </a>
      </div>
      <div className="shrink-0 px-4 py-4 text-[11px] text-vino-100/50">Portal del empleado · Roge RMSA</div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-64 shrink-0 lg:block">{Sidebar}</aside>
      <BarraMovil items={movil} extra={
        <div className="space-y-2">
          <Contactanos tipo="empleado" variante="tarjeta" />
          <a href="/manuales/manual_empleado_roge.pdf" target="_blank" rel="noopener noreferrer" data-testid="mas-guia-portal"
            className="flex items-center justify-center gap-2 rounded-2xl border border-vino-100 bg-vino-50 px-3 py-3 text-sm font-semibold text-vino-800">
            <HelpCircle className="h-4 w-4" /> Cómo usar mi portal
          </a>
          <div className="rounded-2xl bg-vino-900 py-2 text-white"><RogeMascota testid="gato-roge-mas" /></div>
        </div>} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white/95 px-3 backdrop-blur-md sm:px-6 lg:h-16">
          <div className="ml-1"><RogeMascotaHeader /></div>
          <div className="hidden text-sm text-slate-500 sm:block">Bienvenido(a), <span className="font-semibold text-vino-800">{user.name}</span></div>
          <div className="ml-auto">
            <MenuUsuario rol="Empleado" testid="portal-menu" logoutTestid="portal-logout" etiqueta={<span className="hidden sm:block">Mi cuenta</span>} />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-0">
          {user.is_active === false && (
            <div data-testid="banner-solo-lectura" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 sm:px-6 lg:px-8">
              <Lock className="h-4 w-4 shrink-0" />
              Su cuenta está en modo <strong>solo lectura</strong>: puede consultar su información y documentos, pero no modificarlos.
            </div>
          )}
          {correcciones.length > 0 && (
            <div data-testid="banner-correcciones" className="flex flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 sm:px-6 lg:px-8">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="flex-1">HUMAN te pide volver a cargar {correcciones.length === 1 ? "un documento" : `${correcciones.length} documentos`}: {correcciones.map((d) => d.categoria).join(", ")}.</span>
              <Link2 to={correcciones.some((d) => !d.beneficiario_id) ? "/documentos" : "/beneficiarios"} data-testid="banner-correcciones-ir" className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700">Ver y corregir</Link2>
            </div>
          )}
          <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8"><Outlet /></div>
        </main>
        <OfertaPassword />
      </div>
    </div>
  );
}
